import React from "react";
import { Icon } from "../components/Icon";
import { isPlausibleEmail, formatPhone } from "../utils";
import { fieldKeyOf, groupRegistrationFields } from "../registrationFields";
import { INTERPRETER_LANGUAGE_KEY } from "../validateInfoStep";
import type {
  FormData,
  InterpreterLanguage,
  RegistrationField
} from "../types";

/** One affordance for every field on the step, so Name, Email, Phone and custom fields all
 *  announce required-ness the same way (AB#9868). */
export const FieldAffordance: React.FC<{ required: boolean }> = ({ required }) => (
  <span className={required ? "field-required" : "field-optional"}>
    {required ? "(required)" : "(optional)"}
  </span>
);

export interface YourInfoProps {
  form: FormData;
  setForm: React.Dispatch<React.SetStateAction<FormData>>;
  subscribe: boolean;
  setSubscribe: React.Dispatch<React.SetStateAction<boolean>>;
  registrationFields: RegistrationField[];
  onShowSmsInfo: () => void;
  interpreterServicesEnabled: boolean;
  interpreterLanguages: InterpreterLanguage[];
  showEmailSubscribeOption: boolean;
  subscribePrompt?: string | null;
  showSmsConsentOption: boolean;
  fieldMessages: Record<string, string>;
  /** AB#10352: MeetingInfo.speakersCanRegister. The "speaker list" lead copy is only
   *  shown when the meeting accepts speaker registration. Without it the line is
   *  dropped, since kiosks also serve project meetings with no clerk. */
  speakerRegistrationEnabled: boolean;
}

export const YourInfo: React.FC<YourInfoProps> = ({
  form,
  setForm,
  subscribe,
  setSubscribe,
  registrationFields,
  onShowSmsInfo,
  interpreterServicesEnabled,
  interpreterLanguages,
  showEmailSubscribeOption,
  subscribePrompt,
  showSmsConsentOption,
  fieldMessages,
  speakerRegistrationEnabled
}) => {
  const update = <K extends keyof FormData>(k: K, v: FormData[K]) =>
    setForm((d) => ({ ...d, [k]: v }));
  const emailValid = isPlausibleEmail(form.email);

  const setCustomField = (defId: number, value: string) => {
    setForm((d) => ({
      ...d,
      customFields: { ...(d.customFields ?? {}), [String(defId)]: value }
    }));
  };
  const customBoolValue = (defId: number) =>
    (form.customFields?.[String(defId)] || "") === "true";
  const customStringValue = (defId: number) =>
    form.customFields?.[String(defId)] ?? "";

  const messageFor = (field: RegistrationField) => fieldMessages[fieldKeyOf(field)];
  const interpreterMessage = fieldMessages[INTERPRETER_LANGUAGE_KEY];

  const hint = (field: RegistrationField, id: string) => {
    const message = messageFor(field);
    if (!message) return null;
    return (
      <span className="field-hint" role="alert" id={`${id}-error`}>
        {message}
      </span>
    );
  };

  const renderTextField = (
    field: RegistrationField,
    id: string,
    label: string,
    value: string,
    onChange: (next: string) => void
  ) => (
    <div className="field" key={fieldKeyOf(field)} data-testid={`field-${field.key}`}>
      <label htmlFor={id}>
        {label} <FieldAffordance required={field.required} />
      </label>
      <input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
      />
      {hint(field, id)}
    </div>
  );

  const renderEmailField = (field: RegistrationField) => (
    <div className="field" key="email" data-testid="field-email">
      <label htmlFor="mk-em">
        Email <FieldAffordance required={field.required} />
      </label>
      <input
        id="mk-em"
        type="email"
        value={form.email}
        onChange={(e) => {
          const val = e.target.value;
          if (showEmailSubscribeOption) {
            if (!form.email && val) setSubscribe(true);
            else if (form.email && !val.trim()) setSubscribe(false);
          }
          update("email", val);
        }}
        autoComplete="off"
      />
      {showEmailSubscribeOption && (
        <div
          className={`sms-consent ${emailValid ? "" : "muted"}`}
          data-testid="email-subscribe-row"
        >
          <label>
            <input
              type="checkbox"
              checked={subscribe}
              onChange={(e) => setSubscribe(e.target.checked)}
              disabled={!emailValid}
            />
            <span className="sms-copy">
              {subscribePrompt?.trim() ||
                "Email me future agendas and meeting reminders."}
            </span>
          </label>
        </div>
      )}
      {hint(field, "mk-em")}
    </div>
  );

  const renderPhoneField = (field: RegistrationField) => (
    <div
      className="field"
      key="phone"
      data-testid="phone-field"
      data-attend={form.attend}
    >
      <label htmlFor="mk-ph">
        Phone <FieldAffordance required={field.required} />
      </label>
      <input
        id="mk-ph"
        type="tel"
        value={form.phone}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "").slice(0, 15);
          const val = formatPhone(digits);
          if (!form.phone && val) {
            setForm((d) => ({ ...d, phone: val, smsConsent: true }));
            return;
          }
          if (form.phone && !val.trim()) {
            setForm((d) => ({ ...d, phone: val, smsConsent: false }));
            return;
          }
          update("phone", val);
        }}
        autoComplete="off"
        placeholder="+1 555 213 4471"
      />
      {showSmsConsentOption && (
        <div className={`sms-consent ${form.phone ? "" : "muted"}`}>
          <label>
            <input
              type="checkbox"
              checked={form.smsConsent}
              onChange={(e) => update("smsConsent", e.target.checked)}
              disabled={!form.phone}
            />
            <span className="sms-copy">
              Text me an update when my item is active.
            </span>
          </label>
          <button
            type="button"
            className="info-btn"
            onClick={onShowSmsInfo}
            aria-label="View SMS terms and privacy info"
          >
            <Icon name="help" size={20} />
          </button>
        </div>
      )}
      {hint(field, "mk-ph")}
    </div>
  );

  const renderCustomField = (field: RegistrationField) => {
    const inputId = `mk-cf-${field.id}`;
    const labelText = field.name;

    switch (field.type) {
      case "Boolean":
        return (
          <div className="field" key={fieldKeyOf(field)}>
            <div className="sms-consent" data-testid={`custom-field-${field.id}`}>
              <label>
                <input
                  id={inputId}
                  type="checkbox"
                  checked={customBoolValue(field.id)}
                  onChange={(e) =>
                    setCustomField(field.id, e.target.checked ? "true" : "false")
                  }
                />
                <span className="sms-copy">
                  {labelText} <FieldAffordance required={field.required} />
                </span>
              </label>
            </div>
            {hint(field, inputId)}
          </div>
        );
      case "List":
        return (
          <div className="field" key={fieldKeyOf(field)}>
            <label htmlFor={inputId}>
              {labelText} <FieldAffordance required={field.required} />
            </label>
            <select
              id={inputId}
              className="custom-field-select"
              value={customStringValue(field.id)}
              onChange={(e) => setCustomField(field.id, e.target.value)}
              required={field.required}
              data-testid={`custom-field-${field.id}`}
            >
              <option value="" disabled>
                Select an option…
              </option>
              {(field.options ?? []).map((opt) => (
                <option key={opt.id} value={String(opt.id)}>
                  {opt.label}
                </option>
              ))}
            </select>
            {hint(field, inputId)}
          </div>
        );
      default: {
        const htmlType =
          field.htmlInputType === "number"
            ? "number"
            : field.htmlInputType === "date"
            ? "date"
            : "text";
        return (
          <div className="field" key={fieldKeyOf(field)}>
            <label htmlFor={inputId}>
              {labelText} <FieldAffordance required={field.required} />
            </label>
            <input
              id={inputId}
              type={htmlType}
              value={customStringValue(field.id)}
              onChange={(e) => setCustomField(field.id, e.target.value)}
              required={field.required}
              autoComplete="off"
              data-testid={`custom-field-${field.id}`}
            />
            {hint(field, inputId)}
          </div>
        );
      }
    }
  };

  const renderField = (field: RegistrationField) => {
    switch (field.key) {
      case "name":
        return renderTextField(field, "mk-fn", "Name", form.first, (v) =>
          update("first", v)
        );
      case "firstName":
        return renderTextField(field, "mk-fn", "First name", form.first, (v) =>
          update("first", v)
        );
      case "lastName":
        return renderTextField(field, "mk-ln", "Last name", form.last, (v) =>
          update("last", v)
        );
      case "address":
        return renderTextField(field, "mk-addr", "Address", form.address, (v) =>
          update("address", v)
        );
      case "zip":
        return renderTextField(field, "mk-zip", "ZIP code", form.zip, (v) =>
          update("zip", v)
        );
      case "email":
        return renderEmailField(field);
      case "phone":
        return renderPhoneField(field);
      default:
        return renderCustomField(field);
    }
  };

  const rows = groupRegistrationFields(registrationFields);

  return (
    <div className="info-stage">
      <div className="info">
        <div className="lead">
          <h2>Tell us who you are</h2>
          {speakerRegistrationEnabled && (
            <p data-testid="info-lead-copy">
              We use this to add you to the speaker list. Your contact info is
              shared only with the meeting clerk.
            </p>
          )}
        </div>
        <div className="form">
          {rows.map((row) =>
            row.kind === "pair" ? (
              <div
                className="field-row"
                data-testid="name-split"
                key={fieldKeyOf(row.fields[0])}
              >
                {row.fields.map(renderField)}
              </div>
            ) : (
              renderField(row.field)
            )
          )}

          {interpreterServicesEnabled && interpreterLanguages.length > 0 && (
            <fieldset
              className="interpreter-section"
              data-testid="interpreter-section"
            >
              <legend className="interpreter-legend">
                Would you like to request an interpreter?
              </legend>
              <div className="interpreter-toggle">
                <label
                  className={`interpreter-option ${
                    !form.interpreterRequested ? "active" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="interpreterRequested"
                    value="no"
                    checked={!form.interpreterRequested}
                    onChange={() =>
                      setForm((d) => ({
                        ...d,
                        interpreterRequested: false,
                        interpreterLanguage: ""
                      }))
                    }
                  />
                  No
                </label>
                <label
                  className={`interpreter-option ${
                    form.interpreterRequested ? "active" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="interpreterRequested"
                    value="yes"
                    checked={form.interpreterRequested}
                    onChange={() =>
                      setForm((d) => ({
                        ...d,
                        interpreterRequested: true
                      }))
                    }
                  />
                  Yes
                </label>
              </div>
              {form.interpreterRequested && (
                <div className="field interpreter-language-field">
                  <label htmlFor="mk-interpreter-lang">
                    Choose your preferred language
                  </label>
                  <select
                    id="mk-interpreter-lang"
                    className="custom-field-select"
                    value={form.interpreterLanguage}
                    onChange={(e) =>
                      update("interpreterLanguage", e.target.value)
                    }
                    aria-required="true"
                    aria-invalid={Boolean(interpreterMessage)}
                    aria-describedby={
                      interpreterMessage
                        ? "mk-interpreter-lang-error interpreter-help"
                        : "interpreter-help"
                    }
                  >
                    <option value="" disabled>
                      Select a language…
                    </option>
                    {interpreterLanguages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name} ({lang.autoglossonym})
                      </option>
                    ))}
                  </select>
                  {interpreterMessage && (
                    <span
                      className="field-hint"
                      role="alert"
                      id="mk-interpreter-lang-error"
                    >
                      {interpreterMessage}
                    </span>
                  )}
                  <span id="interpreter-help" className="field-hint-static">
                    Language preference will help assign an interpreter if
                    available.
                  </span>
                </div>
              )}
            </fieldset>
          )}
        </div>
      </div>
    </div>
  );
};
